import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { SiteProvider } from "./context/SiteContext";
import { AuthProvider } from "./context/AuthContext";
import { Header } from "./components/site/Header";
import { Footer } from "./components/site/Footer";
import { WhatsAppFloat, ConsentBanner } from "./components/site/Floating";
import Home from "./pages/public/Home";
import CategoryPage from "./pages/public/CategoryPage";
import ExperienceDetail from "./pages/public/ExperienceDetail";
import { Destinations, DestinationDetail } from "./pages/public/Destinations";
import { About, Contact, Policy } from "./pages/public/StaticPages";
import NotFound from "./pages/public/NotFound";
import { Blog, BlogPost } from "./pages/public/Blog";
import { Posts, PostEditor } from "./pages/admin/Posts";
import { AdminLayout, Login, ForgotPassword, ResetPassword, RequirePerm } from "./pages/admin/Layout";
import Dashboard from "./pages/admin/Dashboard";
import { Experiences, ExperienceEditor } from "./pages/admin/Experiences";
import Resource from "./pages/admin/Resource";
import Departures from "./pages/admin/Departures";
import Bookings from "./pages/admin/Bookings";
import Enquiries from "./pages/admin/Enquiries";
import { Customers, Reports, Logs } from "./pages/admin/Misc";
import Content, { Seo } from "./pages/admin/Content";
import { CATEGORIES } from "./lib/api";

function PublicLayout() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 300);
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  const onDetail = CATEGORIES.some((c) => pathname.startsWith(c.detail + "/") || pathname.startsWith(c.path + "/"));
  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-[60] focus:bg-white focus:p-3">Skip to content</a>
      <Header />
      <main id="main" className="flex-1"><Outlet /></main>
      <Footer />
      {!onDetail && <WhatsAppFloat />}
      <ConsentBanner />
    </div>
  );
}

const P = (perm, el) => <RequirePerm perm={perm}>{el}</RequirePerm>;

export default function App() {
  return (
    <SiteProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/experiences" element={<CategoryPage category="" />} />
              {CATEGORIES.map((c) => <Route key={c.key} path={c.path} element={<CategoryPage category={c.key} />} />)}
              {CATEGORIES.map((c) => <Route key={c.key + "d"} path={`${c.detail}/:slug`} element={<ExperienceDetail category={c.key} />} />)}
              <Route path="/heritage-walks/:slug" element={<ExperienceDetail category="heritage-walks" />} />
              <Route path="/destinations" element={<Destinations />} />
              <Route path="/destinations/:slug" element={<DestinationDetail />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<Policy k="privacy" />} />
              <Route path="/terms" element={<Policy k="terms" />} />
              <Route path="/cancellation-policy" element={<Policy k="cancellation" />} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route path="/admin/login" element={<Login />} />
            <Route path="/admin/forgot-password" element={<ForgotPassword />} />
            <Route path="/admin/reset-password" element={<ResetPassword />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="experiences" element={P("content", <Experiences />)} />
              <Route path="experiences/:id" element={P("content", <ExperienceEditor />)} />
              <Route path="destinations" element={P("content", <Resource name="destinations" />)} />
              <Route path="testimonials" element={P("content", <Resource name="testimonials" />)} />
              <Route path="faqs" element={P("content", <Resource name="faqs" />)} />
              <Route path="users" element={P("users", <Resource name="users" />)} />
              <Route path="departures" element={P("bookings", <Departures />)} />
              <Route path="bookings" element={P("bookings", <Bookings />)} />
              <Route path="enquiries" element={P("bookings", <Enquiries type="general" />)} />
              <Route path="corporate" element={P("bookings", <Enquiries type="corporate" />)} />
              <Route path="customers" element={P("bookings", <Customers />)} />
              <Route path="posts" element={P("content", <Posts />)} />
              <Route path="posts/:id" element={P("content", <PostEditor />)} />
              <Route path="content" element={P("content", <Content />)} />
              <Route path="seo" element={P("content", <Seo />)} />
              <Route path="reports" element={P("reports", <Reports />)} />
              <Route path="logs" element={P("settings", <Logs />)} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" toastOptions={{ className: "rounded-none border border-black" }} />
      </AuthProvider>
    </SiteProvider>
  );
}
